import { Outlet, useParams } from 'react-router-dom';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import ReactPaginate from 'react-paginate';
import Avatar from 'funifier-nice-avatar';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import { RebrandShell } from 'components/rebrand/RebrandShell';
import { IconPlus } from 'components/rebrand/icons';
import { useAuth } from 'context/AuthContext';
import { getEquieGestor } from 'services/dashboard.service';

import { MinhaEquipeDetail } from './components/detail';
import './styles.scss';

function TeamAvatar({ item }: { item: any }) {
  if (item.avatar_type) {
    return item?.image?.medium?.url ? (
      <img src={item.image.medium.url} alt={item.nome} className="rb-galcard__img" />
    ) : (
      <span>{item.nome?.[0]}</span>
    );
  }
  return item.avatar ? <Avatar className="rb-galcard__img" {...item.avatar} /> : <span>{item.nome?.[0]}</span>;
}

export default function MinhaEquipe() {
  const params = useParams();

  const [list, setList] = useState<any[]>();
  const [page, setPage] = useState(0);
  const [activePag, setActivePag] = useState(0);
  const [showDetail, setShowDetail] = useState('');
  const [id, setId] = useState<any>(false);
  const [loadingList, setLoadingList] = useState(true);

  const searchRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const isRootUser = useMemo(() => (typeof id?.extra?.id === 'string' ? false : true), [id?.extra?.id]);

  const loadData = useCallback(
    async (e?: any, itemPage?: number) => {
      if (!user) return;
      const payload = id?.extra?.id ? id.extra.id : user?.extra.id;
      if (e) e.preventDefault();

      try {
        const rangeItem = itemPage ? itemPage * 20 : 0;
        setActivePag(itemPage || 0);
        setLoadingList(true);
        setList([]);

        const data = await getEquieGestor(payload, searchRef.current?.value || '', rangeItem);
        setPage(data.range.pages);
        setList(data.result);
        setLoadingList(false);
      } catch (e) {}
    },
    [id?.extra?.id, user],
  );

  useEffect(() => {
    loadData();
  }, [user, id, loadData]);

  if (showDetail) return <MinhaEquipeDetail onClose={() => setShowDetail('')} id={showDetail} isFromTeam={!id} />;

  if (params.id) return <Outlet />;

  return (
    <RebrandShell
      title={isRootUser ? 'Minha Equipe' : `Equipe de ${id?.extra?.nome_guerra || ''}`}
      subtitle="Os colaboradores sob a sua supervisão."
    >
      {!isRootUser && (
        <button type="button" className="rb-back" onClick={() => setId(false)} aria-label="Voltar" style={{ marginBottom: 20 }}>
          <RiArrowLeftSLine size={24} />
        </button>
      )}

      <div className="rb-team-toolbar">
        <p className="rb-team-toolbar__count">
          Lista de colaboradores <span>({list?.length || 0})</span>
        </p>
        <form className="rb-team-toolbar__search" onSubmit={e => loadData(e)}>
          <input ref={searchRef} className="rb-input" placeholder="Pesquise por nome ou chave" inputMode="search" />
          <button type="submit" className="rb-btn-primary">
            Buscar
          </button>
        </form>
      </div>

      {loadingList && (
        <div className="rb-hero-grid">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="rb-skeleton" style={{ height: 280 }} />
          ))}
        </div>
      )}

      {!loadingList && list?.length === 0 && <div className="rb-empty">Nenhum resultado encontrado.</div>}

      {!loadingList && !!list?.length && (
        <div className="rb-hero-grid">
          {list.map((item: any) => (
            <div
              key={item?._id}
              className="rb-hero rb-rise"
              style={{ ['--acc' as any]: 'var(--blue-primary)', ['--acc-glow' as any]: 'rgba(26,171,227,.55)' }}
              onClick={() => setShowDetail(item.matricula)}
              role="button"
              tabIndex={0}
            >
              <div className="rb-hero__inner">
                <div className="rb-hero__avatar">
                  <div className="rb-hero__avatar-ring rb-galcard__avatar">
                    <TeamAvatar item={item} />
                  </div>
                </div>
                <div className="rb-hero__name">{item.nome}</div>
                <div className="rb-hero__role">{item.diretoria}</div>
                <div className="rb-galcard__meta">{item.funcao}</div>
                <div className="rb-galcard__meta">Matrícula {item.matricula}</div>
                <div className="rb-hero__spacer" />
                <button type="button" className="rb-vermais">
                  <IconPlus size={15} /> Ver mais
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {page > 1 && (
        <div className="rb-pagination">
          <ReactPaginate
            activeClassName={'item active '}
            breakClassName={'item break-me '}
            breakLabel={'...'}
            containerClassName={'pagination'}
            disabledClassName={'disabled-page'}
            nextClassName={'item next '}
            pageClassName={'item pagination-page '}
            previousClassName={'item previous'}
            initialPage={0}
            forcePage={activePag}
            nextLabel={<IoIosArrowForward style={{ fontSize: 18 }} />}
            onPageChange={data => loadData(null, data.selected)}
            pageRangeDisplayed={3}
            pageCount={page}
            previousLabel={<IoIosArrowBack style={{ fontSize: 18 }} />}
          />
        </div>
      )}

      <Outlet />
    </RebrandShell>
  );
}
